import type { ToolContent } from "./tool-content";
import { getDePdfToolBySlug } from "./de-pdf-catalog";

interface DePdfSeoOverride {
  h1Title: string;
  seoTitle: string;
  seoDescription: string;
  intro: string;
  faq?: { q: string; a: string }[];
}

const DE_PDF_SEO_DATA: Record<string, DePdfSeoOverride> = {
  "pdf-zusammenfuegen": {
    h1Title: "PDF zusammenfügen kostenlos — Mehrere PDF-Dateien verbinden",
    seoTitle: "PDF zusammenfügen kostenlos online | Picamini.online",
    seoDescription: "PDF zusammenfügen kostenlos online ohne Software-Installation und ohne Server-Upload. Füge mehrere PDF-Dateien zu einem Dokument im Browser zusammen.",
    intro: "Kombiniere zwei oder mehr PDF-Dateien zu einem einzigen geordneten Dokument kostenlos online. Ordne Seiten neu an, wähle Seitenbereiche und lade das Ergebnis sofort datenschutzkonform herunter.",
    faq: [
      {
        q: "Ist das Zusammenfügen von PDFs auf Picamini.online sicher?",
        a: "Absolut. Das Zusammenführen erfolgt zu 100% lokal in deinem eigenen Browser mittels JavaScript. Deine vertraulichen Dokumente werden niemals auf externe Server hochgeladen."
      },
      {
        q: "Gibt es ein Limit bei der Anzahl der zusammenzufügenden PDFs?",
        a: "Nein, es gibt keine künstlichen Einschränkungen. Du kannst so viele PDF-Dateien verbinden, wie der Arbeitsspeicher deines Geräts verarbeiten kann."
      }
    ]
  },
  "pdf-teilen": {
    h1Title: "PDF teilen & Seiten trennen online kostenlos",
    seoTitle: "PDF teilen online kostenlos — Seiten trennen | Picamini.online",
    seoDescription: "PDF teilen kostenlos online — extrahiere einzelne Seiten oder trenne große PDF-Dateien in separate Dokumente ohne Server-Upload.",
    intro: "Trenne Seiten oder extrahiere benutzerdefinierte Seitenbereiche ganz einfach in neue einzelne PDF-Dokumente – schnell, sicher und vollständig privat.",
    faq: [
      {
        q: "Kann ich auch nur eine bestimmte Seite aus einer PDF speichern?",
        a: "Ja, du kannst den exakten Seitenbereich angeben oder per Klick einzelne Seiten auswählen, die in ein neues PDF exportiert werden sollen."
      }
    ]
  },
  "pdf-organisieren": {
    h1Title: "PDF-Seiten organisieren & neu anordnen kostenlos",
    seoTitle: "PDF-Seiten organisieren kostenlos online | Picamini.online",
    seoDescription: "Ordne PDF-Seiten visuell per Drag & Drop neu an, lösche unbenötigte Blätter oder drehe Seiten im Browser.",
    intro: "Bringe Struktur in deine PDF-Dokumente: Sortiere Seiten neu per Drag & Drop, entferne leere Seiten oder drehe Falschausrichtungen mühelos um.",
  },
  "pdf-drehen": {
    h1Title: "PDF drehen online kostenlos — Seiten ausrichten",
    seoTitle: "PDF drehen online kostenlos — Seiten ausrichten | Picamini.online",
    seoDescription: "Drehe einzelne oder alle Seiten deiner PDF-Datei dauerhaft um 90, 180 oder 270 Grad direkt im Browser.",
    intro: "Korrigiere falsch gescannte oder auf dem Kopf stehende PDF-Seiten im Handumdrehen dauerhaft und lade die saubere Datei herunter.",
  },
  "pdf-seiten-extrahieren": {
    h1Title: "PDF-Seiten extrahieren kostenlos — Einzelne Seiten speichern",
    seoTitle: "PDF-Seiten extrahieren kostenlos online | Picamini.online",
    seoDescription: "Extrahiere bestimmte Seiten aus einem großen PDF-Dokument und erstelle eine neue, kompakte PDF-Datei ohne Server-Upload.",
    intro: "Wähle genau die Seiten aus, die du benötigst, und erstelle daraus in Sekunden ein neues, schlankes PDF-Dokument.",
  },
  "pdf-seiten-loeschen": {
    h1Title: "PDF-Seiten löschen kostenlos — Seiten entfernen",
    seoTitle: "PDF-Seiten löschen kostenlos online | Picamini.online",
    seoDescription: "Entferne leere, doppelte oder unerwünschte Seiten aus deinem PDF-Dokument direkt im Browser.",
    intro: "Lösche störende oder überflüssige Seiten dauerhaft aus deiner PDF-Datei mit nur einem Klick.",
  },
  "pdf-bearbeiten": {
    h1Title: "PDF online bearbeiten kostenlos — Text, Bilder & Formulare",
    seoTitle: "PDF online bearbeiten kostenlos | Picamini.online",
    seoDescription: "PDF online bearbeiten kostenlos. Füge Text hinzu, unterschreibe Dokumente, zeichne Formen, füge Bilder ein und markiere Inhalte ohne App-Download.",
    intro: "Ergänze Anmerkungen, Textfelder, Freihandzeichnungen, digitale Unterschriften und Bilder bequem und intuitiv in deinen PDF-Dateien.",
  },
  "pdf-seitenzahlen": {
    h1Title: "PDF Seitenzahlen hinzufügen kostenlos — Nummerieren",
    seoTitle: "PDF Seitenzahlen hinzufügen kostenlos online | Picamini.online",
    seoDescription: "Füge automatische Seitenzahlen in Kopf- oder Fußzeile deiner PDF-Dokumente ein. Passe Schriftart und Position an.",
    intro: "Nummeriere deine Berichte, Skripte und Hausarbeiten professionell mit anpassbaren Seitenzahlen in Kopf- oder Fußzeile.",
  },
  "pdf-wasserzeichen": {
    h1Title: "PDF Wasserzeichen hinzufügen kostenlos — Text & Logo",
    seoTitle: "PDF Wasserzeichen hinzufügen kostenlos online | Picamini.online",
    seoDescription: "Füge Text- oder Logo-Wasserzeichen auf deinen PDF-Seiten kostenlos ein. Passe Transparenz, Position und Winkel direkt im Browser an.",
    intro: "Schütze deine Dokumente durch das Einprägen von Wasserzeichen mit individuellem Text oder Firmenlogo auf allen PDF-Seiten.",
  },
  "pdf-komprimieren": {
    h1Title: "PDF komprimieren & verkleinern ohne Qualitätsverlust",
    seoTitle: "PDF komprimieren kostenlos online — Dateigröße verkleinern | Picamini.online",
    seoDescription: "PDF komprimieren kostenlos online — reduziere die PDF-Dateigröße für den E-Mail-Versand ohne spürbaren Schärfeverlust. 100% datenschutzkonform.",
    intro: "Reduziere die Dateigröße deiner PDF-Dateien für den schnellen E-Mail-Anhang und Online-Uploads bei hoher Textschärfe.",
  },
  "pdf-graustufen": {
    h1Title: "PDF in Schwarz-Weiß umwandeln kostenlos — Druckkosten sparen",
    seoTitle: "PDF in Schwarz-Weiß umwandeln kostenlos | Picamini.online",
    seoDescription: "Wandle farbige PDF-Dateien in Graustufen (Schwarz-Weiß) um, um Tinte und Druckerkosten zu sparen.",
    intro: "Konvertiere Bunt-PDFs in Graustufen, um Toner und Tinte beim Ausdrucken drastisch zu reduzieren.",
  },
  "pdf-reparieren": {
    h1Title: "Beschädigte PDF reparieren kostenlos — Wiederherstellung",
    seoTitle: "Beschädigte PDF reparieren kostenlos online | Picamini.online",
    seoDescription: "Stelle unlesbare oder beschädigte PDF-Dateien direkt im Browser wieder her.",
    intro: "Repariere beschädigte PDF-Dokumente und retten Sie den enthaltenen Text und die Bilder ohne Datenverlust.",
  },
  "bild-in-pdf-umwandeln": {
    h1Title: "JPG in PDF umwandeln kostenlos — Bilder zu PDF konvertieren",
    seoTitle: "JPG in PDF umwandeln kostenlos (Bild zu PDF) | Picamini.online",
    seoDescription: "Wandle JPG, PNG, WEBP und GIF-Bilder kostenlos in eine PDF-Datei um. Füge mehrere Fotos in ein sauberes PDF ohne Wasserzeichen zusammen.",
    intro: "Konvertiere JPG, PNG, WEBP oder GIF-Fotos in ein einziges geordnetes PDF-Dokument. Passe Ränder und Ausrichtung an und lade es direkt herunter.",
  },
  "word-in-pdf-umwandeln": {
    h1Title: "Word in PDF umwandeln kostenlos — DOCX zu PDF",
    seoTitle: "Word in PDF umwandeln kostenlos online | Picamini.online",
    seoDescription: "Word in PDF umwandeln kostenlos online. Wandle DOCX und DOC-Dateien direkt im Browser in saubere PDFs um.",
    intro: "Konvertiere deine Word-Dokumente in ein sauberes PDF-Format, das auf allen Geräten exakt gleich dargestellt wird.",
  },
  "excel-in-pdf-umwandeln": {
    h1Title: "Excel in PDF umwandeln kostenlos — XLSX zu PDF",
    seoTitle: "Excel in PDF umwandeln kostenlos online | Picamini.online",
    seoDescription: "Wandle Excel-Arbeitsblätter (XLSX) direkt im Browser in gut formatierte PDF-Tabellen um.",
    intro: "Konvertiere deine Excel-Tabellen in übersichtliche PDF-Dateien, die sofort gedruckt oder geteilt werden können.",
  },
  "powerpoint-in-pdf-umwandeln": {
    h1Title: "PowerPoint in PDF umwandeln kostenlos — PPTX zu PDF",
    seoTitle: "PowerPoint in PDF umwandeln kostenlos online | Picamini.online",
    seoDescription: "Konvertiere PowerPoint-Präsentationsfolien (PPTX) direkt im Browser in ein leichtes PDF.",
    intro: "Wandle Präsentationsfolien aus PowerPoint in kompakte PDFs um, die auf jedem Bildschirm perfekt angezeigt werden.",
  },
  "html-in-pdf-umwandeln": {
    h1Title: "HTML in PDF umwandeln kostenlos — Webcode zu PDF",
    seoTitle: "HTML in PDF umwandeln kostenlos online | Picamini.online",
    seoDescription: "Speichere HTML-Code oder Webseiten-Inhalte direkt im Browser als formatiertes PDF.",
    intro: "Konvertiere Quellcode oder Webseitentext in ein sauberes PDF-Dokument.",
  },
  "text-in-pdf-umwandeln": {
    h1Title: "Text (TXT) in PDF umwandeln kostenlos",
    seoTitle: "Text in PDF umwandeln kostenlos online | Picamini.online",
    seoDescription: "Wandle einfache Textdateien (.txt) in formatierte PDF-Dokumente um.",
    intro: "Konvertiere einfache Textdateien in saubere PDFs mit gut lesbaren Schriften.",
  },
  "pdf-in-jpg-umwandeln": {
    h1Title: "PDF in JPG umwandeln kostenlos — Seiten als Bild extrahieren",
    seoTitle: "PDF in JPG umwandeln kostenlos (PDF zu Bild) | Picamini.online",
    seoDescription: "PDF in JPG oder PNG Bilder kostenlos umwandeln. Extrahiere jede Seite deines PDF-Dokuments als hochauflösendes Bild direkt im Browser.",
    intro: "Wandle jede Seite deines PDF-Dokuments in hochauflösende JPG- oder PNG-Bilddateien um – ideal zum Weiterverarbeiten oder Präsentieren.",
  },
  "pdf-in-word-umwandeln": {
    h1Title: "PDF in Word umwandeln kostenlos — PDF zu DOCX bearbeitbar",
    seoTitle: "PDF in Word umwandeln kostenlos online | Picamini.online",
    seoDescription: "PDF in Word umwandeln kostenlos online. Extrahiere den Inhalt deines PDFs in eine bearbeitbare Word-Datei unter Beibehaltung des Textes.",
    intro: "Konvertiere PDF-Dateien in bearbeitbare Microsoft Word-Dokumente, um Texte schnell anzupassen.",
  },
  "pdf-in-excel-umwandeln": {
    h1Title: "PDF in Excel umwandeln kostenlos — Tabellen extrahieren",
    seoTitle: "PDF in Excel umwandeln kostenlos online | Picamini.online",
    seoDescription: "Extrahiere Datentabellen aus PDF-Dateien direkt in eine bearbeitbare Excel-Tabelle (XLSX).",
    intro: "Wandle PDF-Tabellen in bearbeitbare Excel-Tabellenblattdateien um, um Zahlen schnell zu analysieren.",
  },
  "pdf-text-extrahieren": {
    h1Title: "PDF Text extrahieren kostenlos — Text herauskopieren",
    seoTitle: "PDF Text extrahieren kostenlos online | Picamini.online",
    seoDescription: "Extrahiere den reinen Textgehalt aus deinen PDF-Dokumenten in eine leichte .txt-Datei.",
    intro: "Hole den geschriebenen Inhalt aus PDF-Dateien ohne störende Formatierungen als Reintext heraus.",
  },
  "pdf-schuetzen": {
    h1Title: "PDF mit Passwort schützen kostenlos — Verschlüsseln",
    seoTitle: "PDF mit Passwort schützen kostenlos online | Picamini.online",
    seoDescription: "PDF mit Passwort schützen kostenlos online. Verschlüssele vertrauliche PDF-Dateien mit 128-Bit-Sicherheit direkt auf deinem Gerät.",
    intro: "Sichere vertrauliche PDF-Dokumente mit einem starken Passwortschutz. 100% lokale Verschlüsselung garantiert, dass deine Daten deinen Browser nie verlassen.",
  },
  "pdf-entsperren": {
    h1Title: "PDF Passwort entfernen & entsperren kostenlos",
    seoTitle: "PDF Passwort entfernen & entsperren kostenlos | Picamini.online",
    seoDescription: "PDF Passwort entfernen kostenlos online — hebe Passwortschutz und Drucksperren bei eigenen PDF-Dateien ohne Server-Upload auf.",
    intro: "Entferne Öffnungs-Passwörter oder Drucksperren von PDF-Dokumenten, die dir gehören, schnell und sicher.",
  },
  "pdf-unterschreiben": {
    h1Title: "PDF online unterschreiben kostenlos — Digitale Signatur",
    seoTitle: "PDF online unterschreiben kostenlos | Picamini.online",
    seoDescription: "Füge deine persönliche Unterschrift oder ein Signatur-Bild kostenlos auf PDF-Verträgen und Formularen ein.",
    intro: "Zeichne deine Freihand-Unterschrift oder lade ein Stempelbild hoch, um Verträge und Anträge ohne Ausdrucken zu unterzeichnen.",
  },
  "pdf-ocr-texterkennung": {
    h1Title: "PDF OCR Texterkennung kostenlos — Gescannte PDFs durchsuchbar machen",
    seoTitle: "PDF OCR Texterkennung kostenlos online | Picamini.online",
    seoDescription: "Wandle gescannte Bild-PDFs mit optischer Zeichenerkennung (OCR) in durchsuchbaren Text um.",
    intro: "Mache gescannte Dokumente durchsuchbar und kopierbar dank moderner OCR-Texterkennung direkt im Browser.",
  }
};

export function getDePdfToolContent(slug: string, fallbackName: string): ToolContent {
  const tool = getDePdfToolBySlug(slug);
  const deSlug = tool ? tool.slug : slug;
  const enSlug = tool ? tool.enSlug : slug;

  const override = DE_PDF_SEO_DATA[deSlug] || DE_PDF_SEO_DATA[enSlug] || {};
  const name = fallbackName;

  return {
    seoTitle: override.seoTitle || `${name} Kostenlos Online | Picamini.online`,
    seoDescription: override.seoDescription || `PDF-Tool ${name.toLowerCase()} kostenlos online. Verarbeite deine PDF-Dateien 100% datenschutzkonform und lokal im Browser.`,
    h1Title: override.h1Title || `${name} Online Kostenlos — 100% Lokal im Browser`,
    intro: override.intro || `Nutze unser Werkzeug für ${name.toLowerCase()} schnell, privat und unbegrenzt. Alle Schritte werden direkt in deinem Browser verarbeitet.`,
    whatIs: [
      `Das Tool ${name} gehört zur PDF Toolkit Pro Suite von Picamini.online, die entwickelt wurde, um Dokumentenverarbeitung schnell und absolut vertraulich zu gestalten.`,
      `Im Gegensatz zu traditionellen Online-Konvertern, die sensible Dokumente auf fremde Cloud-Server hochladen, führt unser Tool alle Berechnungen lokal auf deinem Prozessor aus.`,
      `Das garantiert maximale Verarbeitungsgeschwindigkeit ohne Upload-Wartezeiten und lückenlosen Datenschutz.`
    ],
    howTo: [
      "Wähle oder ziehe deine PDF-Datei in das Upload-Feld des Werkzeugs.",
      "Passe die gewünschten Optionen (wie Seiten, Reihenfolge, Qualität oder Passwort) an.",
      "Klicke auf den Haupt-Button, um das Dokument in Sekundenschnelle zu verarbeiten.",
      "Lade deine fertig verarbeitete PDF-Datei sofort auf dein Gerät herunter."
    ],
    features: [
      "100% Kostenlos ohne Einschränkungen bei Dateianzahl oder Dateigröße",
      "Garantierter Datenschutz: Deine Dokumente verlassen niemals dein Gerät",
      "Extrem schnelle browser-lokale Verarbeitung",
      "Kompatibel mit allen Betriebssystemen (Windows, Mac, Linux, iOS & Android)",
      "Keine Registrierung, kein E-Mail-Zwang und keine Softwareinstallation"
    ],
    benefits: [
      "Vollständige DSGVO-Konformität, da keine Daten über das Internet gesendet werden",
      "Sofortige Verfügbarkeit ohne Warteschlangen auf Servern",
      "Erhält die Schärfe und Formatierung deiner Originaldateien"
    ],
    proTips: [
      "Für hochsensible Verträge oder Finanzdokumente bietet dieses browserbasierte Tool die Sicherheit, dass nichts ins Netz gelangt.",
      "Aktiviere im Browser automatische Downloads, wenn du mehrere Dateien auf einmal verarbeitest."
    ],
    faq: override.faq || [
      {
        q: `Ist die Nutzung von ${name} sicher?`,
        a: "Ja, absolut sicher. Da die Verarbeitung direkt in deinem eigenen Browser abläuft, werden deine Dateien nicht über das Internet hochgeladen."
      },
      {
        q: "Muss ich mich registrieren oder etwas bezahlen?",
        a: "Nein. Picamini.online stellt alle PDF-Tools zu 100% kostenlos und ohne Registrierung zur Verfügung."
      }
    ]
  };
}
